// Console filter utility to reduce excessive logging in development

// Rate limiting for console messages
const messageCounts = new Map();
const RATE_LIMIT_WINDOW = 10000; // 10 seconds
const MAX_MESSAGES_PER_WINDOW = 3; // Max 3 messages per 10 seconds for same pattern

// Helper function to check if a message should be filtered
function shouldFilterMessage(message) {
  if (typeof message !== "string") return false;

  // Filter out expected HTTP errors for API endpoints (401, 403, 404)
  // These are common when endpoints don't exist or user lacks permissions
  // Check for axios error format: "GET http://... 401 (UNAUTHORIZED)"
  if (
    (message.includes("404 (NOT FOUND)") ||
      message.includes("403 (FORBIDDEN)") ||
      message.includes("401 (UNAUTHORIZED)") ||
      message.match(/\s(401|403|404)\s\(/)) && // Match " 401 (" or " 403 (" etc.
    (message.includes("/api/") || message.includes("api/") || message.includes("http://") || message.includes("https://"))
  ) {
    return true;
  }

  // Filter out session expiration errors (expected when token expires)
  if (
    message.includes("session has expired") ||
    message.includes("session expired") ||
    message.includes("Your session has expired") ||
    message.includes("Please log in again")
  ) {
    return true;
  }

        // More aggressive filtering - only show important messages
        const filterPatterns = [
          // Browser extension errors
          "runtime.lastError",
          "background-redux-new.js",
          "LastPass",
          "Cannot find menu item",
          "WebSocket connection to",
          "WebSocket is closed before the connection is established",
          "Unchecked runtime.lastError",
          "Uncaught (in promise)",
          "Add Item",
          "Open Web UI for Ollama",
          "Download the React DevTools",
          "react.dev/link/react-devtools",
          "reactjs.org/link/react-devtools",
          "Violation",
          "handler took",
          "No tab with id:",
          "Invalid frameId for foreground frameId:",
          "The page keeping the extension port is moved into back/forward cache",
          "message channel is closed",
          "Error: No tab with id:",
          "Error: Invalid frameId for foreground frameId:",
          "extension port is moved into back/forward cache",
          "message channel is closed",
          "background-redux-new.js:1",
          "background-redux-new.js:2",
          "ee @ background-redux-new.js:",
          "f @ background-redux-new.js:",
          "(anonymous) @ background-redux-new.js:",
          "The page keeping the extension port is moved into back/forward cache, so the message channel is closed",
          
          // Additional aggressive patterns for console spam
          "Cannot create item with duplicate id",
          "Fill - ",
          "Edit - ",
          "Copy first name",
          "Copy last name",
          "Copy address line",
          "Copy city/town",
          "Copy ZIP/postal code",
          "Copy email address",
          "Copy phone number",
          "separator",
          "Add Password",
          "Add Address",
          "Add Payment Card",
          "Add other item",
          "Save all entered data",
          "Generate Secure Password",

    // Auth and API spam - more comprehensive patterns
    "Using stored JWT token",
    "AuthService",
    "AuthContext",
    "Using JWT headers for API request",
    "Session expiry check:",
    "🔍 Scraping API Response:",
    "📊 Scoping API Response:",
    "🧹 Cleanup API Response:",
    "🔍 Scraping API Error:",
    "📊 Scoping API Error:",
    "🧹 Cleanup API Error:",

    // Progressive results spam
    "ProgressiveResultsContext]",
    "ProgressiveResults]",
    "Loading active sessions",
    "Processing",
    "Backend response",
    "Processed sessions",
    "Polling session",

    // Dashboard spam
    "DashboardPage component initialized",
    "DashboardPage: Checking if DashboardOverview should render",
    "Final visible sections:",
    "Filtering section",
    "Hiding",
    "ScraperRunsSummary component rendering",
    "ScraperRunsSummary RBAC Debug",
    "FreshnessWidget rendered with data",

    // AlertBanner spam
    "AlertBanner:",
    "Component rendered",
    "useEffect triggered",
    "User found, fetching alerts",
    "Starting fetchActiveAlerts",
    "Token found",
    "Making API request",
    "API response received",
    "API data received",
    "Setting alerts",
    "Rendering with state",
  ];

  const shouldFilter = filterPatterns.some((pattern) =>
    message.includes(pattern)
  );

  if (shouldFilter) {
    // Rate limiting for filtered messages
    const now = Date.now();
    const key = message.substring(0, 50); // Use first 50 chars as key

    if (!messageCounts.has(key)) {
      messageCounts.set(key, []);
    }

    const timestamps = messageCounts.get(key);
    // Remove timestamps older than the window
    const recentTimestamps = timestamps.filter(
      (ts) => now - ts < RATE_LIMIT_WINDOW
    );

    if (recentTimestamps.length >= MAX_MESSAGES_PER_WINDOW) {
      return true; // Rate limited, filter out
    }

    recentTimestamps.push(now);
    messageCounts.set(key, recentTimestamps);
  }

  return shouldFilter;
}

const DEBUG_ENABLED =
  import.meta.env.DEV &&
  (import.meta.env.VITE_DEBUG_API === "true" ||
    localStorage.getItem("VITE_DEBUG_API") === "true" ||
    new URLSearchParams(window.location.search).get("debug") === "true");

// Filter out browser extension errors
const originalError = console.error;
console.error = (...args) => {
  // Check all arguments, not just the first one
  for (const arg of args) {
    let message = arg;
    
    // Handle error objects (e.g., AxiosError)
    if (arg && typeof arg === 'object') {
      // Check for AxiosError structure
      if (arg.response && arg.response.status) {
        const status = arg.response.status;
        const url = arg.config?.url || arg.request?.responseURL || '';
        // Filter out expected 401/403/404 for API endpoints
        if ((status === 401 || status === 403 || status === 404) && url.includes('/api/')) {
          return; // Don't log expected API errors
        }
      }
      // Check for Error object with message
      if (arg.message) {
        message = arg.message;
        // Also check if it's a session expiration error
        if (
          message.includes("session has expired") ||
          message.includes("session expired") ||
          message.includes("Your session has expired") ||
          message.includes("Please log in again")
        ) {
          return; // Don't log session expiration errors
        }
      } else if (arg.toString) {
        message = arg.toString();
      }
    }
    
    // Convert to string if not already
    if (typeof message !== 'string') {
      message = String(message);
    }
    
    // Check for axios error format: "GET http://... 401 (UNAUTHORIZED)" or similar
    if (typeof message === 'string') {
      const axiosErrorPattern = /(GET|POST|PUT|DELETE|PATCH)\s+https?:\/\/[^\s]+\s+(401|403|404)\s+\(/;
      if (axiosErrorPattern.test(message) && message.includes('/api/')) {
        return; // Don't log axios 401/403/404 errors for API endpoints
      }
    }
    
    // Filter out browser extension errors and other noise
    if (shouldFilterMessage(message)) {
      return; // Don't log these
    }
  }

  originalError.apply(console, args);
};

// Filter out excessive API logging unless debug is enabled
const originalLog = console.log;
console.log = (...args) => {
  const message = args[0];

  // In development, be more aggressive about filtering
  if (import.meta.env.DEV) {
    // Filter out browser extension errors and other noise
    if (shouldFilterMessage(message)) {
      return; // Don't log these
    }

    // Additional aggressive filtering for development
    if (message && typeof message === "string") {
      // Filter out any message that looks like repetitive logging
      if (
        message.includes("2025-10-24T") &&
        (message.includes("AuthService") ||
          message.includes("AuthContext") ||
          message.includes("Using JWT") ||
          message.includes("Session expiry"))
      ) {
        return; // Don't log these repetitive auth messages
      }
    }
  }

  // Only log API-related messages if debug is enabled
  if (
    typeof message === "string" &&
    (message.includes("system.js:") || message.includes("api.js:"))
  ) {
    if (!DEBUG_ENABLED) {
      return; // Don't log these unless debug is enabled
    }
  }

  // Filter out i18next missing key warnings unless debug is enabled
  if (
    typeof message === "string" &&
    message.includes("i18next::translator: missingKey")
  ) {
    if (!DEBUG_ENABLED) {
      return; // Don't log missing key warnings unless debug is enabled
    }
  }

  originalLog.apply(console, args);
};

// Filter out browser extension warnings
const originalWarn = console.warn;
console.warn = (...args) => {
  const message = args[0];

  // In development, be more aggressive about filtering
  if (import.meta.env.DEV) {
    // Filter out browser extension warnings and other noise
    if (shouldFilterMessage(message)) {
      return; // Don't log these
    }

    // Additional aggressive filtering for development
    if (message && typeof message === "string") {
      // Filter out any message that looks like repetitive logging
      if (
        message.includes("2025-10-24T") &&
        (message.includes("AuthService") ||
          message.includes("AuthContext") ||
          message.includes("Using JWT") ||
          message.includes("Session expiry"))
      ) {
        return; // Don't log these repetitive auth messages
      }
    }
  }

  originalWarn.apply(console, args);
};

// Also filter console.info and console.debug
const originalInfo = console.info;
console.info = (...args) => {
  const message = args[0];

  // In development, be more aggressive about filtering
  if (import.meta.env.DEV) {
    if (shouldFilterMessage(message)) {
      return;
    }

    // Additional aggressive filtering for development
    if (message && typeof message === "string") {
      // Filter out any message that looks like repetitive logging
      if (
        message.includes("2025-10-24T") &&
        (message.includes("AuthService") ||
          message.includes("AuthContext") ||
          message.includes("Using JWT") ||
          message.includes("Session expiry"))
      ) {
        return; // Don't log these repetitive auth messages
      }
    }
  }

  originalInfo.apply(console, args);
};

const originalDebug = console.debug;
console.debug = (...args) => {
  const message = args[0];

  // In development, be more aggressive about filtering
  if (import.meta.env.DEV) {
    if (shouldFilterMessage(message)) {
      return;
    }

    // Additional aggressive filtering for development
    if (message && typeof message === "string") {
      // Filter out any message that looks like repetitive logging
      if (
        message.includes("2025-10-24T") &&
        (message.includes("AuthService") ||
          message.includes("AuthContext") ||
          message.includes("Using JWT") ||
          message.includes("Session expiry"))
      ) {
        return; // Don't log these repetitive auth messages
      }
    }
  }

  originalDebug.apply(console, args);
};

// Override global error handlers to catch extension errors
const originalOnError = window.onerror;
window.onerror = function (message, source, lineno, colno, error) {
  if (shouldFilterMessage(message)) {
    return true; // Prevent default error handling
  }
  if (originalOnError) {
    return originalOnError(message, source, lineno, colno, error);
  }
  return false;
};

// Override unhandled promise rejection handler
const originalOnUnhandledRejection = window.onunhandledrejection;
window.onunhandledrejection = function (event) {
  const message = event.reason?.message || event.reason?.toString() || "";
  if (shouldFilterMessage(message)) {
    event.preventDefault(); // Prevent default error handling
    return;
  }
  if (originalOnUnhandledRejection) {
    originalOnUnhandledRejection(event);
  }
};

export { DEBUG_ENABLED };

// Helper function to show debug mode instructions
export const showDebugInstructions = () => {
  console.log(`
🔧 Debug Mode Instructions:
To enable detailed logging, use one of these methods:
1. Add ?debug=true to the URL
2. Set localStorage.setItem('debugMode', 'true') in console
3. Set VITE_DEBUG_API=true in .env file

Current debug status: ${DEBUG_ENABLED ? "ENABLED" : "DISABLED"}
  `);
};

// Show instructions on startup in development
if (import.meta.env.DEV) {
  setTimeout(() => {
    showDebugInstructions();
  }, 1000);
}
